import { useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useInboxes, useInboxMutations } from '@/hooks/use-inboxes';
import { useBusinessUnits } from '@/hooks/use-business-units';
import { helpdeskLabel } from '@/constants/helpdesk-labels';

/** 'Channel::Api' -> 'Api'. Candy stores the Rails class name; the prefix is noise here. */
const shortChannel = (channelType: string) =>
  channelType.replace(/^Channel::/, '');

/**
 * Inboxes that belong to no business unit.
 *
 * Every inbox list on this page was nested under a business unit, so an inbox with no
 * `bu_id` appeared in none of them. Unassigning therefore looked like deleting: the row
 * vanished, and the only way back was to know it still existed and re-create it — which
 * does not work, because a destination is fixed at creation and the old inbox is still
 * holding it. This section is where those inboxes go, so unassign means what it says.
 *
 * It is also the landing place for inboxes that never had a unit: an account created
 * before multi-brand, or an inbox made in candy rather than here.
 *
 * Renders nothing when there are none — an empty section on a tidy account is noise.
 */
export function UnassignedInboxesSection() {
  const { data: all = [], isLoading } = useInboxes();
  const { data: units = [] } = useBusinessUnits();
  const { assignBusinessUnit } = useInboxMutations();
  const [busyId, setBusyId] = useState<number | null>(null);

  const inboxes = all.filter((i) => i.bu_id == null);

  // An inactive unit is not offered as a destination for a move: assigning an inbox to a
  // unit nobody is using would hide it again, one level down.
  const options = units.filter((u) => u.status === 'active');

  const assign = async (inboxId: number, name: string, buId: string) => {
    const unit = options.find((u) => String(u.id) === buId);
    if (!unit) return;
    setBusyId(inboxId);
    try {
      await assignBusinessUnit(inboxId, unit.id);
      toast.success(`${name} is now under ${unit.name}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not assign');
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) return <Skeleton className='h-8 w-full' />;
  if (inboxes.length === 0) return null;

  return (
    <div className='space-y-3 rounded-xl border border-dashed bg-card/40 p-4'>
      <div className='flex items-baseline justify-between gap-3'>
        <span className='text-sm font-medium'>Not in a business unit</span>
        <span className='text-xs text-muted-foreground'>
          {inboxes.length} inbox{inboxes.length === 1 ? '' : 'es'} — tickets
          arriving here trace back to the account, not to a brand.
        </span>
      </div>

      <ul className='space-y-2'>
        {inboxes.map((inbox) => {
          const destination = inbox.destination;
          return (
            <li
              key={inbox.id}
              className='flex items-center gap-2 rounded-md border bg-card px-3 py-2'
            >
              <span className='min-w-0 flex-1'>
                <span className='block truncate text-sm font-medium'>
                  {inbox.name}
                </span>
                {destination ? (
                  <span className='block truncate font-mono text-xs text-muted-foreground'>
                    {destination.label
                      ? `${destination.label} · ${destination.identifier}`
                      : destination.identifier}
                  </span>
                ) : (
                  <span className='block truncate text-xs text-muted-foreground'>
                    No destination — nothing routes here yet.
                  </span>
                )}
              </span>
              {destination && (
                <>
                  <Badge variant='secondary' className='shrink-0'>
                    {helpdeskLabel(destination.provider)}
                  </Badge>
                  {destination.channel_type && (
                    <Badge variant='outline' className='shrink-0'>
                      {destination.channel_type}
                    </Badge>
                  )}
                </>
              )}
              <Badge variant='outline' className='shrink-0'>
                {shortChannel(inbox.channel_type)}
              </Badge>
              <Select
                disabled={busyId === inbox.id || options.length === 0}
                onValueChange={(v) => assign(inbox.id, inbox.name, v)}
              >
                <SelectTrigger size='sm' className='w-[170px] shrink-0'>
                  <SelectValue placeholder='Assign to…' />
                </SelectTrigger>
                <SelectContent>
                  {options.map((unit) => (
                    <SelectItem key={unit.id} value={String(unit.id)}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
